import * as React from 'react';
import * as _ from 'lodash';
import asyncDispatch from '../../../../../services/mini/asyncDispatch';
import globalEvents from '../../../../../const/globalEvents';
import { connect, ConnectedProps } from 'react-redux';
import { IStore } from '../../../../../redux/initialState';
import * as actions from '../../../../../redux/codeMetaData/actions.codeMetaData';
import { IParseAllScriptsParam } from '../../../../../redux/codeMetaData/actions.codeMetaData';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controlled as ReactCodeMirror } from 'react-codemirror2';
import editorSettings from '../../../../../const/editorSettings';

const cmOptions = {
    readOnly: true,
    mode: { name: "alanKeywords", json: true, globalVars: true },
    tabSize: editorSettings.tabSize,
    indentUnit: editorSettings.indentUnit,
    autoCloseBrackets: true,
    autoCloseTags: true,
    lineWrapping: false,
    showCursorWhenSelecting: true,
};

function getTextBetweenLines(text, startLine, endLine) {
    const lines = text.split('\n');
    const selectedLines = lines.slice(startLine - 1, endLine); // startLine - 1 because arrays are 0-indexed
    return selectedLines.join('\n');
}

const transformFnInfoPopupMouseStateKeeper = {
    state: false
};

type TransformFnInfoPopupProps = PropsFromRedux;

function TransformFnInfoPopup({
    project,
    methodsMap,
    parseAllScripts
}: TransformFnInfoPopupProps) {

    const [text, setText] = useState(null);
    const [x, setX] = useState(null);
    const [y, setY] = useState(null);
    const [height, setHeight] = useState(0);
    const [fileName, setFileName] = useState('');

    const posRef = useRef({ x: null, y: null });

    useEffect(() => {
        parseAllScripts({ project });
    }, []);

    const onVariableHoverListener = (e) => {
        const fnName = e?.detail?.name;
        const fnInfo = methodsMap[fnName];

        if (fnInfo) {
            for (let i = 0; i < project?.scripts?.length; i++) {
                if (project.scripts[i]?.name === fnInfo.fileName) {

                    const endLine = fnInfo.loc?.end?.line;
                    const startLine = fnInfo.leadingComments?.length > 0 ?
                        fnInfo.leadingComments[fnInfo.leadingComments.length - 1]?.loc?.start?.line || fnInfo.loc?.start?.line :
                        fnInfo.loc?.start?.line;

                    const height = endLine - startLine;

                    setText(getTextBetweenLines(project.scripts[i]?.text, startLine, endLine))
                    if (posRef.current.x === null && posRef.current.y === null) {
                        setX(e?.detail?.x);
                        setY(e?.detail?.y);
                        posRef.current = { x: e?.detail?.x, y: e?.detail?.y };
                    }
                    setFileName(fnInfo.fileName);
                    setHeight(height);
                    break;
                }
            }
        }

    };

    const onCloseCb = useCallback(() => {
        setText(null);
        setX(null);
        setY(null);
        setFileName(null);
        posRef.current = { x: null, y: null };
        transformFnInfoPopupMouseStateKeeper.state = false;
    }, []);


    const onCloseListener = () => {
        if (!transformFnInfoPopupMouseStateKeeper.state) {
            onCloseCb();
        }
    };


    useEffect(() => {
        window.addEventListener(globalEvents.CM_ON_VARIABLE_MOUSE_IN, onVariableHoverListener);
        window.addEventListener(globalEvents.CM_ON_VARIABLE_MOUSE_OUT, onCloseListener);
        return () => {
            window.removeEventListener(globalEvents.CM_ON_VARIABLE_MOUSE_IN, onVariableHoverListener);
            window.removeEventListener(globalEvents.CM_ON_VARIABLE_MOUSE_OUT, onCloseListener);
        }
    }, []);

    let xVal;
    let yVal;

    if (_.isNumber(x) && _.isNumber(y)) {
        xVal = x;
        if (xVal + 480 > window.innerWidth) {
            xVal = xVal - 480 - 30;
        } else {
            xVal = xVal + 80;
        }
        yVal = y - 150;
        if (yVal + 370 > window.innerHeight - 44) {
            yVal = yVal - window.innerHeight - 44 - (yVal + 370);
        }
    }

    const style = {
        left: `${xVal}px`,
        top: `${yVal}px`,
        height: `${height * 30}px`
    }

    const onMouseEnter = () => {
        transformFnInfoPopupMouseStateKeeper.state = true;
    }

    const onMouseLeave = () => {
        transformFnInfoPopupMouseStateKeeper.state = false;
    }

    return (
        <div
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className="transform-fn-info-popup" style={style}>
            {fileName && <div className="transform-fn-info-popup__header">
                {fileName}
                <i className="icon icon-alan-close-rounded transform-fn-info-popup__close-icon" onClick={onCloseCb} />
            </div>}

            {text &&
                <div className="transform-fn-info-popup__body">
                    <ReactCodeMirror
                        autoCursor={true}
                        value={text}
                        options={cmOptions}
                        onBeforeChange={(editor, data, value) => {
                        }}
                    />
                </div>}
        </div>
    );
}


function mapStateToProps({ scriptEditor, user, codeMetaData, projectsInfo, gitHubIntegration }: IStore) {
    return {
        project: projectsInfo.currentProject,
        methodsMap: codeMetaData.methodsMap,
    };
}


function mapDispatchToProps(dispatch) {
    return {
        parseAllScripts(param: IParseAllScriptsParam) {
            asyncDispatch(actions.parseAllScripts, dispatch, param);
        },
    };
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(TransformFnInfoPopup);